export const TRADE_CLOSE_WS_TYPES = new Set([
  'trade_closed',
  'trade_sl_hit',
  'trade_tp_hit',
]);

export const TRADE_CLOSED_BUS_EVENT = 'trade-closed';

const toFiniteNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const isTradeCloseMessage = (message) => (
  Boolean(message && TRADE_CLOSE_WS_TYPES.has(message.type))
);

export const normalizeTradeCloseMessage = (message) => {
  if (!isTradeCloseMessage(message)) return null;

  const payload = message.payload || {};
  const tradeId = payload.trade_id ?? payload.tradeId ?? null;
  if (tradeId == null || tradeId === '') return null;

  let closeReason = payload.close_reason || null;
  if (!closeReason) {
    if (message.type === 'trade_sl_hit') closeReason = 'STOP_LOSS';
    if (message.type === 'trade_tp_hit') closeReason = 'TAKE_PROFIT';
  }

  const exitPrice = toFiniteNumber(
    payload.exit_price ?? payload.close_price ?? payload.price
  );
  const profitLoss = toFiniteNumber(payload.profit_loss);
  const netPnl = toFiniteNumber(payload.net_pnl);
  const pnl = toFiniteNumber(payload.pnl ?? payload.net_pnl ?? payload.profit_loss);

  return {
    tradeId: String(tradeId),
    status: payload.status || 'CLOSED',
    closeReason,
    exitPrice,
    closedAt: payload.closed_at ?? payload.timestamp ?? payload.effective_at ?? null,
    pnl,
    profitLoss,
    netPnl,
    instrumentToken: payload.instrument_token ?? null,
    reason: payload.reason ?? null,
    source: payload.source ?? null,
    eventType: message.type,
  };
};

export const createTradeCloseIdempotencyStore = () => {
  const processed = new Set();

  return {
    tryClaim(tradeId) {
      if (tradeId == null || tradeId === '') return false;
      const id = String(tradeId);
      if (processed.has(id)) return false;
      processed.add(id);
      return true;
    },
    has(tradeId) {
      if (tradeId == null || tradeId === '') return false;
      return processed.has(String(tradeId));
    },
    clear() {
      processed.clear();
    },
    size() {
      return processed.size;
    },
  };
};

export const removeClosedTradeFromPositions = (openPositionTrades, tradeId) => {
  if (!openPositionTrades?.trades || tradeId == null) return openPositionTrades;

  const id = String(tradeId);
  const trades = openPositionTrades.trades.filter((trade) => String(trade.id) !== id);
  if (trades.length === openPositionTrades.trades.length) return openPositionTrades;

  return {
    ...openPositionTrades,
    trades,
  };
};

export const filterClosedTradesFromPositions = (openPositionTrades, closedTradeIds) => {
  if (!openPositionTrades?.trades || !closedTradeIds?.size) return openPositionTrades;

  const trades = openPositionTrades.trades.filter(
    (trade) => !closedTradeIds.has(String(trade.id))
  );
  if (trades.length === openPositionTrades.trades.length) return openPositionTrades;

  return {
    ...openPositionTrades,
    trades,
  };
};

export const formatCloseReasonLabel = (closeReason) => {
  if (closeReason === 'STOP_LOSS') return 'Stop Loss';
  if (closeReason === 'TAKE_PROFIT') return 'Take Profit';
  return closeReason || '-';
};

export const buildHistoryTradeFromCloseEvent = (closedEvent, sourceTrade = null) => {
  if (!closedEvent?.tradeId) return null;

  const base = sourceTrade ? { ...sourceTrade } : {};
  const pnlValue = closedEvent.pnl ?? closedEvent.netPnl ?? closedEvent.profitLoss;

  return {
    ...base,
    id: sourceTrade?.id ?? closedEvent.tradeId,
    status: 'CLOSED',
    close_price: closedEvent.exitPrice ?? sourceTrade?.close_price ?? null,
    close_reason: closedEvent.closeReason ?? sourceTrade?.close_reason ?? null,
    closed_at: closedEvent.closedAt ?? sourceTrade?.closed_at ?? null,
    profitLoss: pnlValue ?? sourceTrade?.profitLoss ?? null,
    profit_loss: closedEvent.profitLoss ?? sourceTrade?.profit_loss ?? pnlValue ?? null,
    net_pnl: closedEvent.netPnl ?? sourceTrade?.net_pnl ?? pnlValue ?? null,
    instrument_token: closedEvent.instrumentToken ?? sourceTrade?.instrument_token ?? null,
    remarks: closedEvent.reason ?? sourceTrade?.remarks ?? null,
  };
};

export const upsertClosedTradeInHistory = (historyTrades, closedEvent, sourceTrade = null) => {
  const nextTrade = buildHistoryTradeFromCloseEvent(closedEvent, sourceTrade);
  if (!nextTrade) return historyTrades;

  const existingTrades = Array.isArray(historyTrades?.trades) ? historyTrades.trades : [];
  const tradeId = String(nextTrade.id);
  const index = existingTrades.findIndex((trade) => String(trade.id) === tradeId);

  let trades;
  if (index >= 0) {
    trades = existingTrades.slice();
    trades[index] = { ...existingTrades[index], ...nextTrade };
  } else {
    trades = [nextTrade, ...existingTrades];
  }

  return {
    ...(historyTrades || {}),
    trades,
  };
};

export const applySavedSlTpToTrade = (trade, patch) => {
  if (!trade || !patch) return trade;

  const next = { ...trade };
  if (Object.prototype.hasOwnProperty.call(patch, 'takeprofitPrice')) {
    const value = toFiniteNumber(patch.takeprofitPrice);
    next.takeprofitPrice = value ?? patch.takeprofitPrice;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'stoplossPrice')) {
    const value = toFiniteNumber(patch.stoplossPrice);
    next.stoplossPrice = value ?? patch.stoplossPrice;
  }
  if (Object.prototype.hasOwnProperty.call(patch, 'newLimitOrder')) {
    next.openPrice = toFiniteNumber(patch.newLimitOrder) ?? patch.newLimitOrder;
  }
  return next;
};

export const applySavedSlTpToPositions = (openPositionTrades, tradeId, patch) => {
  if (!openPositionTrades?.trades || tradeId == null || !patch) return openPositionTrades;

  const id = String(tradeId);
  let changed = false;
  const trades = openPositionTrades.trades.map((trade) => {
    if (String(trade.id) !== id) return trade;
    changed = true;
    return applySavedSlTpToTrade(trade, patch);
  });

  if (!changed) return openPositionTrades;
  return { ...openPositionTrades, trades };
};
